import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Phone, Calendar, Check, ArrowLeft, Flame, Wind, Shield, Clock } from 'lucide-react';
import { generateCityLocalBusinessSchema, generateCityServiceSchema, generateBreadcrumbSchema, generateReviewSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';

const cityData = {
  lexington: { name: 'Lexington', county: 'Fayette County' },
  nicholasville: { name: 'Nicholasville', county: 'Jessamine County' },
  danville: { name: 'Danville', county: 'Boyle County' },
  richmond: { name: 'Richmond', county: 'Madison County' },
  paris: { name: 'Paris', county: 'Bourbon County' },
  harrodsburg: { name: 'Harrodsburg', county: 'Mercer County' },
  winchester: { name: 'Winchester', county: 'Clark County' },
  georgetown: { name: 'Georgetown', county: 'Scott County' },
  midway: { name: 'Midway', county: 'Woodford County' },
  lawrenceburg: { name: 'Lawrenceburg', county: 'Anderson County' },
  versailles: { name: 'Versailles', county: 'Woodford County' },
  frankfort: { name: 'Frankfort', county: 'Franklin County' },
  lancaster: { name: 'Lancaster', county: 'Garrard County' },
  cynthiana: { name: 'Cynthiana', county: 'Harrison County' },
  'mt-sterling': { name: 'Mt Sterling', county: 'Montgomery County' },
  wilmore: { name: 'Wilmore', county: 'Jessamine County' },
  'stamping-ground': { name: 'Stamping Ground', county: 'Scott County' },
  berea: { name: 'Berea', county: 'Madison County' }
};

const CityPage = () => {
  const { city } = useParams();
  const data = cityData[city];

  // Generate schemas for valid city pages
  const localBusinessSchema = data ? generateCityLocalBusinessSchema(data.name, data.county, city) : null;
  const serviceSchema = data ? generateCityServiceSchema(data.name) : null;
  const breadcrumbSchema = data ? generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Service Areas', url: 'https://comfortmasterheating.com/service-areas' },
    { name: data.name, url: `https://comfortmasterheating.com/service-areas/${city}` }
  ]) : null;
  const reviewSchema = data ? generateReviewSchema(data.name) : null;

  // Inject schemas into document head
  useSchemaMarkup(
    data ? [localBusinessSchema, serviceSchema, breadcrumbSchema, reviewSchema] : [],
    `city-${city}`
  );

  if (!data) {
    return (
      <main className="py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-heading font-bold text-4xl text-slate-900 mb-4">City Not Found</h1>
          <p className="text-slate-600 mb-8">The city you are looking for is not in our service area.</p>
          <Link to="/service-areas" className="btn-primary">View All Service Areas</Link>
        </div>
      </main>
    );
  }

  return (
    <main>
      <Helmet>
        <title>AC Company {data.name} | HVAC Services {data.name} KY | ComfortMaster</title>
        <meta name="description" content={`AC repair, HVAC company and heating services in ${data.name}, KY. ComfortMaster provides expert AC replacement, furnace repair, and air conditioning service in ${data.county}. Call (859) 255-0728.`} />
        <meta name="keywords" content={`ac company ${data.name.toLowerCase()}, hvac company ${data.name.toLowerCase()}, ac repair ${data.name.toLowerCase()}, ac service ${data.name.toLowerCase()}, heating repair ${data.name.toLowerCase()}, furnace repair ${data.name.toLowerCase()}, ac replacement ${data.name.toLowerCase()}, ${data.name.toLowerCase()} ky hvac`} />
        <link rel="canonical" href={`https://comfortmasterheating.com/service-areas/${city}`} />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/service-areas" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-6" data-testid="back-to-areas">
            <ArrowLeft size={18} />
            Back to Service Areas
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-8 h-8 text-emerald-400" />
            <span className="text-emerald-400 font-semibold">{data.county}</span>
          </div>
          <h1 className="font-heading font-bold text-5xl md:text-6xl mb-6">
            HVAC Services in {data.name}, KY
          </h1>
          <p className="text-xl text-stone-300 mb-8 max-w-3xl">
            ComfortMaster Heating & Air provides expert residential and commercial HVAC services to {data.name} and the surrounding {data.county} area. With 25+ years of experience, we are your trusted local heating and cooling experts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="city-estimate-btn">
              <Calendar size={20} />
              Get Free Estimate in {data.name}
            </Link>
            <a href="tel:8592550728" className="btn-secondary inline-flex items-center justify-center gap-2 bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900" data-testid="city-call-btn">
              <Phone size={20} />
              (859) 255-0728
            </a>
          </div>
        </div>
      </section>

      {/* Services in City */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Our Services in {data.name}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From emergency repairs to new installations, we offer comprehensive HVAC services to keep your {data.name} home comfortable year-round.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Heating Services */}
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <Flame className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-4">Heating Services in {data.name}</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Furnace Repair & Installation</li>
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Heat Pump Services</li>
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Heating Maintenance</li>
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Emergency Heating Repair</li>
              </ul>
              <Link to="/services/heating" className="text-emerald-600 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
                Learn More <ArrowLeft size={16} className="rotate-180" />
              </Link>
            </div>

            {/* Cooling Services */}
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Wind className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-4">Cooling Services in {data.name}</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />AC Repair & Installation</li>
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Central Air Systems</li>
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />AC Maintenance</li>
                <li className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-600" />Emergency AC Repair</li>
              </ul>
              <Link to="/services/cooling" className="text-emerald-600 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
                Learn More <ArrowLeft size={16} className="rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Why {data.name} Homeowners Choose ComfortMaster
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-emerald-600" />
              </div>
              <h4 className="font-bold text-slate-900 mb-2">Licensed & Insured</h4>
              <p className="text-slate-600 text-sm">Fully licensed Kentucky HVAC contractor</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-emerald-600" />
              </div>
              <h4 className="font-bold text-slate-900 mb-2">Same-Day Service</h4>
              <p className="text-slate-600 text-sm">Fast response times in {data.name}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">25+</span>
              </div>
              <h4 className="font-bold text-slate-900 mb-2">Years Experience</h4>
              <p className="text-slate-600 text-sm">Trusted expertise since 1999</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">5★</span>
              </div>
              <h4 className="font-bold text-slate-900 mb-2">5-Star Reviews</h4>
              <p className="text-slate-600 text-sm">Highly rated by {data.name} customers</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">Ready for HVAC Service in {data.name}?</h2>
          <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
            Contact ComfortMaster today for a free estimate on heating and cooling services in {data.name}, KY.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 font-bold py-3 px-6 rounded-full" data-testid="city-cta-estimate">
              <Calendar size={18} />
              Get Free Estimate
            </Link>
            <a href="tel:8592550728" className="inline-flex items-center justify-center gap-2 bg-emerald-700 text-white font-bold py-3 px-6 rounded-full border-2 border-white/30" data-testid="city-cta-call">
              <Phone size={18} />
              Call (859) 255-0728
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CityPage;
